// AdminEventForm — admin-only create-event form for the Events calendar.
//
// Wired to POST /api/v1/admin/events (CreateEventRequest). Rendered by
// EventsCalendar only when the current user is an admin; the backend
// independently enforces admin (403 otherwise), so this is a UX gate, not the
// security boundary.
//
// Design: 4px grid, semantic tokens, full interactive-state coverage, dark mode
// (per AI_LAB_DESIGN_PRINCIPLES.md). Classes live in ./events-calendar.css.

import { useState } from "react";
import { apiClient, ApiError } from "@/lib/api-client";

interface CreateEventRequest {
  title: string;
  tone: string;
  cadence: string;
  time_start: string;
  time_end: string;
  recurrence_dates: string[];
  location?: string | null;
  note?: string | null;
}

interface Props {
  /** Called after a successful create (with the new event id). */
  onCreated?: (eventId: string) => void;
  onCancel?: () => void;
}

const TONES = [
  { id: "green", label: "Green" },
  { id: "blue", label: "Blue" },
];

export function AdminEventForm({ onCreated, onCancel }: Props) {
  const [title, setTitle] = useState("");
  const [cadence, setCadence] = useState("");
  const [tone, setTone] = useState("green");
  const [timeStart, setTimeStart] = useState("14:00");
  const [timeEnd, setTimeEnd] = useState("15:00");
  const [location, setLocation] = useState("");
  const [note, setNote] = useState("");
  const [dates, setDates] = useState<string[]>([""]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setDateAt = (i: number, value: string) =>
    setDates((prev) => prev.map((d, idx) => (idx === i ? value : d)));
  const addDate = () => setDates((prev) => [...prev, ""]);
  const removeDate = (i: number) =>
    setDates((prev) => (prev.length > 1 ? prev.filter((_, idx) => idx !== i) : prev));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const cleanDates = dates.map((d) => d.trim()).filter(Boolean);
    if (!title.trim() || !cadence.trim()) {
      setError("Title and cadence are required.");
      return;
    }
    if (cleanDates.length === 0) {
      setError("Add at least one occurrence date.");
      return;
    }

    const body: CreateEventRequest = {
      title: title.trim(),
      tone,
      cadence: cadence.trim(),
      time_start: timeStart,
      time_end: timeEnd,
      recurrence_dates: cleanDates,
      location: location.trim() || null,
      note: note.trim() || null,
    };

    setBusy(true);
    try {
      const created = await apiClient<{ id: string }>("/api/v1/admin/events", {
        method: "POST",
        body: JSON.stringify(body),
      });
      onCreated?.(created.id);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setError("You do not have permission to create events.");
      } else {
        setError(err instanceof Error ? err.message : "Failed to create event.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="event-form" onSubmit={handleSubmit} data-testid="admin-event-form">
      <h2 className="event-form__title">New event</h2>

      {error && (
        <p className="event-form__error" role="alert" data-testid="event-form-error">
          {error}
        </p>
      )}

      <label className="event-form__field">
        <span>Title</span>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Claude Code office hours"
          required
        />
      </label>

      <label className="event-form__field">
        <span>Cadence</span>
        <input
          type="text"
          value={cadence}
          onChange={(e) => setCadence(e.target.value)}
          placeholder="Every other Wednesday"
          required
        />
      </label>

      <div className="event-form__row">
        <label className="event-form__field">
          <span>Start</span>
          <input type="time" value={timeStart} onChange={(e) => setTimeStart(e.target.value)} required />
        </label>
        <label className="event-form__field">
          <span>End</span>
          <input type="time" value={timeEnd} onChange={(e) => setTimeEnd(e.target.value)} required />
        </label>
        <label className="event-form__field">
          <span>Tone</span>
          <select value={tone} onChange={(e) => setTone(e.target.value)}>
            {TONES.map((t) => (
              <option key={t.id} value={t.id}>
                {t.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <label className="event-form__field">
        <span>Location (optional)</span>
        <input
          type="text"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          placeholder="Teams · or a room name"
        />
      </label>

      <label className="event-form__field">
        <span>Note (optional)</span>
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} />
      </label>

      <fieldset className="event-form__dates">
        <legend>Occurrence dates</legend>
        {dates.map((d, i) => (
          <div className="event-form__date-row" key={i}>
            <input
              type="date"
              value={d}
              onChange={(e) => setDateAt(i, e.target.value)}
              data-testid="event-date-input"
            />
            <button
              type="button"
              className="event-form__date-remove"
              onClick={() => removeDate(i)}
              disabled={dates.length === 1}
              aria-label="Remove date"
            >
              ×
            </button>
          </div>
        ))}
        <button type="button" className="event-form__add-date" onClick={addDate}>
          + Add another date
        </button>
      </fieldset>

      <div className="event-form__actions">
        <button type="button" className="event-form__btn" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button
          type="submit"
          className="event-form__btn event-form__btn--primary"
          disabled={busy}
          data-testid="event-form-submit"
        >
          {busy ? "Creating…" : "Create event"}
        </button>
      </div>
    </form>
  );
}

export default AdminEventForm;
